import { Component, inject, OnInit, ChangeDetectorRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { ZoneService } from 'src/app/services/modules/instant-rate-manager/zone.service';
import { AlertUtil } from 'src/app/shared/utils/alert-dialog.util';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CustomZoneModalComponent } from '../custom-zone-modal/custom-zone-modal.component';
import { BulkSuburbModalComponent } from '../bulk-suburb-modal/bulk-suburb-modal.component';

interface Zone {
  id: number;
  name: string;
  suburbs: any[];
  isEditing: boolean;
  status: 'Complete' | 'Incomplete';
  showAllSuburbs?: boolean;
}

@Component({
  selector: 'app-manage-locations',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule],
  templateUrl: './manage-locations.component.html',
  styleUrl: './manage-locations.component.scss',
})
export class ManageLocationsComponent implements OnInit {
  private zoneService = inject(ZoneService);
  private cdr = inject(ChangeDetectorRef);
  private modalService = inject(NgbModal);

  @ViewChild('zoneSelect') zoneSelect!: NgSelectComponent;

  /**
   * STATE
   */
  zones: Zone[] = [];
  pageLoading = false;
  isInitialize = false;
  isLoading = false;
  isRefetching = false;
  isInlineLoading = false;
  activeEditZoneId: number | null = null;
  currentSort = '-updated_at';

  isBulkMode = false;
  uploadedFile: File | null = null;
  isUploadingCsv = false;

  selectedZone: any = null;
  zoneOptions: any[] = [];
  isLoadingZoneOptions = false;
  zoneSearchTerm$ = new Subject<string>();

  stagedSuburbs: any[] = [];
  suburbOptions: any[] = [];
  isLoadingSuburbOptions = false;
  suburbSearchTerm$ = new Subject<string>();

  search = '';
  zoneListingSearch$ = new Subject<string>();
  inlineSuburbOptions: any[] = [];
  isLoadingInlineSuburbs = false;
  inlineSuburbSearchTerm$ = new Subject<string>();

  paginationInfo: any = null;

  get isZoneSelected(): boolean {
    return !!this.selectedZone;
  }

  ngOnInit() {
    this.fetchProviderZones();
    this.setupSearchStreams();
    this.loadInitialOptions();
  }

  /**
   * SEARCH STREAMS
   */
  private setupSearchStreams() {
    this.zoneSearchTerm$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      tap(() => (this.isLoadingZoneOptions = true)),
      switchMap(term => this.fetchZoneOptions(term))
    ).subscribe(data => {
      this.zoneOptions = data;
      this.isLoadingZoneOptions = false;
      this.cdr.detectChanges();
    });

    this.suburbSearchTerm$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      tap(() => (this.isLoadingSuburbOptions = true)),
      switchMap(term => this.fetchSuburbOption(term))
    ).subscribe(data => {
      this.suburbOptions = data;
      this.isLoadingSuburbOptions = false;
      this.cdr.detectChanges();
    });

    this.inlineSuburbSearchTerm$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      tap(() => (this.isLoadingInlineSuburbs = true)),
      switchMap(term => this.fetchSuburbOption(term))
    ).subscribe(data => {
      this.inlineSuburbOptions = data.map((opt: { value: any; label: any; }) => ({
        id: opt.value,
        name: opt.label
      }));
      this.isLoadingInlineSuburbs = false;
      this.cdr.detectChanges();
    });

    this.zoneListingSearch$.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(() => {
      this.fetchProviderZones(true, 1);
    });
  }

  /**
   * ACTIONS
   */
  toggleEdit(zone: Zone): void {
    if (this.activeEditZoneId !== null && this.activeEditZoneId !== zone.id) {
      AlertUtil.error('Please save or cancel your current changes first.');
      return;
    }

    zone.isEditing = !zone.isEditing;
    this.activeEditZoneId = zone.isEditing ? zone.id : null;

    if (zone.isEditing) {
      this.isLoadingInlineSuburbs = true;
      this.inlineSuburbOptions = [...zone.suburbs];

      this.fetchSuburbOption('').then(opts => {
        const mapped = opts.map((o: { value: any; label: any; }) => ({ id: o.value, name: o.label }));
        const merged = [...this.inlineSuburbOptions];
        mapped.forEach((opt: { id: any; }) => {
          if (!merged.find(m => m.id === opt.id)) merged.push(opt);
        });
        this.inlineSuburbOptions = merged;
        this.isLoadingInlineSuburbs = false;
        this.cdr.detectChanges();
      });
    }
  }

  async openBulkSuburbModal() {
    if (!this.selectedZone) {
      AlertUtil.error('Please select a zone first.');
      return;
    }

    if (this.zones.length === 0) {
      await AlertUtil.info('You must have at least one active zone to use bulk upload.');
      return;
    }

    const modalRef = this.modalService.open(BulkSuburbModalComponent, { centered: true });
    modalRef.result.then((file: File) => {
      if (file) {
        this.uploadedFile = file;
        this.isBulkMode = true;
        this.stagedSuburbs = [];
        AlertUtil.success(`File "${file.name}" attached. Manual search is now disabled.`);
        this.cdr.detectChanges();
      }
    }, () => { });
  }


  removeBulkFile() {
    this.isBulkMode = false;
    this.uploadedFile = null;
    this.cdr.detectChanges();
  }

  async createZone() {
    if (!this.selectedZone?.value) {
      AlertUtil.error('Please select a valid zone.');
      return;
    }

    this.isLoading = true;
    try {
      let payload: any;
      if (this.isBulkMode && this.uploadedFile) {
        const formData = new FormData();
        formData.append('zone_id', this.selectedZone.value.toString());
        formData.append('file', this.uploadedFile);
        formData.append('is_bulk', 'true');
        payload = formData;
      } else {
        payload = {
          zone_id: this.selectedZone.value,
          location_ids: this.stagedSuburbs.map(s => s.value),
          is_bulk: false
        };
      }
      await this.zoneService.submitZone(payload);
      AlertUtil.success('Operation successful!');
      this.resetForm();
      await this.fetchProviderZones(true, 1);
    } catch (error: any) {
      const errorData = error?.error;
      const errors: string[] = errorData?.errors || [];
      const message = errorData?.message || 'Failed to update zone.';

      if (errors.length > 1) {
        const html = `<p class="mb-2">${message}</p><ul class="text-start ps-3 mb-0">${errors.map((e: string) => `<li>${e}</li>`).join('')}</ul>`;
        AlertUtil.errorConfirm(html, 'Upload Failed');
      } else {
        AlertUtil.error(errors[0] || message);
      }
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  resetForm() {
    this.selectedZone = null;
    this.stagedSuburbs = [];
    this.removeBulkFile();
  }

  /**
   * DATA FETCHING
   */
  async fetchProviderZones(isSilentFetch = false, page: number = 1) {
    if (!isSilentFetch) this.pageLoading = true;
    try {
      const response = await this.zoneService.getProviderZones({
        ordering: this.currentSort,
        page: page,
        page_size: 5,
        is_active: true,
        name: this.search
      });

      this.paginationInfo = {
        count: response.count,
        current_page: response.current_page,
        total_pages: response.total_pages,
        next: response.next,
        previous: response.previous
      };

      this.zones = (response.result || []).map((z: any) => ({
        id: z.id,
        name: z.name,
        suburbs: (z.suburbs || []).map((s: any) => ({ id: s.id, name: s.name })),
        isEditing: false,
        showAllSuburbs: false,
        status: z.suburbs?.length > 0 ? 'Complete' : 'Incomplete'
      }));

      if (this.zones.length > 0) this.isInitialize = true;
    } finally {
      this.pageLoading = false;
      this.isRefetching = false;
      this.cdr.detectChanges();
    }
  }

  async saveZoneChanges(zone: Zone) {
    this.isInlineLoading = true;
    try {
      const payload = {
        zone_id: zone.id,
        location_ids: zone.suburbs.map(s => s.id),
        is_bulk: false
      };
      await this.zoneService.submitZone(payload);
      AlertUtil.success('Zone updated!');
      zone.isEditing = false;
      this.activeEditZoneId = null;
      await this.fetchProviderZones(true, this.paginationInfo?.current_page || 1);
    } finally {
      this.isInlineLoading = false;
      this.cdr.detectChanges();
    }
  }

  cancelEdit(zone: Zone) {
    zone.isEditing = false;
    this.activeEditZoneId = null;
    this.fetchProviderZones(true, this.paginationInfo?.current_page || 1);
  }

  async deleteZone(id: number) {
    const res = await AlertUtil.confirm('Are you sure?', 'This action is permanent.');
    if (!res.isConfirmed) return;
    this.isRefetching = true;
    try {
      await this.zoneService.deleteZone(id);
      AlertUtil.success('Deleted!');
      await this.fetchProviderZones(true, 1);
    } catch {
      AlertUtil.error('Delete failed.');
      this.isRefetching = false;
    }
  }

  /**
   * UI HELPERS
   */
  onZoneSelect(event: any) {
    this.selectedZone = event;
    this.stagedSuburbs = [];
    this.removeBulkFile();
    if (event) {
      this.isLoadingSuburbOptions = true;
      this.fetchSuburbOption('').then(options => {
        this.suburbOptions = options;
        this.isLoadingSuburbOptions = false;
        this.cdr.detectChanges();
      });
    }
  }

  manualCreateZone(term: string) {
    if (this.zoneSelect) { this.zoneSelect.close(); this.zoneSelect.searchTerm = ''; }
    const modalRef = this.modalService.open(CustomZoneModalComponent, { centered: true });
    modalRef.componentInstance.zoneName = term.trim();
    modalRef.result.then(r => { if (r) this.fetchProviderZones(true, 1); }, () => { });
  }

  async initializePopularZones() {
    this.isLoading = true;
    try {
      const response = await this.zoneService.syncZones();
      await this.fetchProviderZones(true, 1);
      const message = response?.data?.message || response?.message || 'Popular zones have been successfully initialized.';
      AlertUtil.success(message);
    } catch (error: any) {
      const errorData = error?.error;
      const message = errorData?.message || error?.message || 'Failed to initialize popular zones. Please try again.';
      AlertUtil.error(message);
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  onPageChange(dir: 'next' | 'prev') {
    const target = dir === 'next' ? this.paginationInfo.current_page + 1 : this.paginationInfo.current_page - 1;
    this.isRefetching = true;
    this.fetchProviderZones(true, target);
  }

  onSortChange(newSort: string) {
    this.currentSort = newSort;
    this.isRefetching = true;
    this.fetchProviderZones(true, 1);
  }

  async fetchZoneOptions(term: string) {
    const response = await this.zoneService.getProviderZones({ page_size: 50, search: term });
    return (response.result || []).map((z: any) => ({ value: z.id, label: z.name }));
  }
  async fetchSuburbOption(term: string) { return await this.zoneService.getSuburb({ name: term, exclude_provider: true }); }
  async loadInitialOptions() { this.zoneOptions = await this.fetchZoneOptions(''); this.cdr.detectChanges(); }
}
